import React, {ReactElement} from "react";
import {get} from 'lodash';
import {DeepKeyof} from "../domain/types";
import {SortOrder} from "../../placement/domains";
import {CaretDownIcon, CaretUpIcon, Checkbox, ChevronDownIcon, ChevronUpIcon, Icon, IconButton} from "evergreen-ui";

export type EnrichedTableStyle = {
    customTableStyle: Object;
    tableContent: string;
    firstColStyle: string;
    trHeader: string;
    trRow: string;
    trRowSelected: string;
    loadingWrapper: string;
}

const defaultStyle: EnrichedTableStyle = {
    customTableStyle: {},
    tableContent: 'overflow-auto flex flex-col flex-grow mb-4 scrollbar-always',
    trHeader: 'text-left select-none text-sm text-gray-portal-table-title',
    firstColStyle: 'pl-4',
    loadingWrapper: 'w-full h-full flex flex-grow items-center',
    trRow: 'border-b hover:bg-gray-200',
    trRowSelected: 'border-b border-gray-200 relative hover:bg-botticelli-100 bg-botticelli-100 text-white',
}

export interface Column<T> {
    name: string;
    span: number;
    hide?: boolean;
    attr: DeepKeyof<T>;
}

export interface SortBy<T> {
    column: DeepKeyof<T>;
    order: SortOrder;
}

export type EnrichTableExpandFeat<T> = {
    setExpandedFunc: ((selected: T) => void);
    shouldShowExpanded: ((item: T) => boolean);
    itemHaveExpand: ((item: T) => boolean);
    expandDisplayFunc: ((item: T) => React.ReactElement);
}

export type EnrichTableMultiSelectFeat<T> = {
    isSelected: (item: T) => boolean;
    shouldDisableSelected: (item: T) => boolean;
    toggleSelect: (item: T) => void;
    toggleAllFeat?: ErichTableMultiSelectToggleAllFeat | null
}

type ErichTableMultiSelectToggleAllFeat = {
    disabled?: boolean;
    isSomeSelected: () => boolean;
    isAllSelected: () => boolean;
    toggle: () => void;
}

export type EnrichTableLoadingFeat = {
    loading: boolean;
    loadingDisplayElement: React.ReactElement;
}

export type RowHighlightFeat<T> = {
    shouldHighlight: (item: T) => boolean;
    highlightColorByItem?: (item: T) => { column: string, row: string };
    highlightCss: {
        column: string;
        row: string
    };
}

export type EnrichTableActionColFeat = {
    actionColumn: ReactElement;
    colSpan: number;
}

type EnrichedTableProps<T> = {
    columns: Readonly<Column<T>[]>;
    styleOverride?: Partial<EnrichedTableStyle>;

    rowsData: T[] | undefined;
    keyColumn: DeepKeyof<T>;
    firstColumn?: DeepKeyof<T> | null;

    sortBy?: SortBy<T> | null;
    setSortBy?: ((selected: Column<T>) => void) | null;

    cellDisplayFunc: (item: T, column: Column<T>, selected: boolean) => string | React.ReactElement;

    selected?: T | null;
    setSelectedFunc?: null | ((selected: T) => void);

    expandFeat?: EnrichTableExpandFeat<T> | null;
    loadingFeat?: EnrichTableLoadingFeat | null;
    rowHighlightFeat?: RowHighlightFeat<T> | null;

    multiselectFeat?: EnrichTableMultiSelectFeat<T> | null;

    actionColFeat?: EnrichTableActionColFeat | null;

    emptyMessage: ReactElement;
    hideOnEmpty?: boolean;
}

export const EnrichedTable = <T, >({
                                       selected,


                                       firstColumn = null,
                                       keyColumn,
                                       columns,
                                       rowsData,

                                       cellDisplayFunc,
                                       setSelectedFunc = null,

                                       expandFeat = null,
                                       loadingFeat = null,
                                       rowHighlightFeat = null,
                                       multiselectFeat = null,
                                       actionColFeat = null,


                                       sortBy = null,
                                       setSortBy = null,
                                       hideOnEmpty = false,
                                       emptyMessage,
                                       styleOverride,
                                   }: EnrichedTableProps<T>) => {

    let style = defaultStyle;
    if (styleOverride) {
        style = {...style, ...styleOverride}
    }
    if (hideOnEmpty && rowsData && rowsData.length === 0) {
        return <div></div>
    }

    return (
        <div className={style.tableContent}
             style={style.customTableStyle}>
            <table className='table table-fixed w-full'>
                <thead>
                <tr className={style.trHeader}>
                    {
                        multiselectFeat !== null &&
                        <th colSpan={1}>
                            {
                                multiselectFeat.toggleAllFeat &&

                                <div className="flex items-center justify-center">
                                    <Checkbox
                                        disabled={multiselectFeat.toggleAllFeat.disabled}
                                        checked={multiselectFeat.toggleAllFeat.isAllSelected()}
                                        indeterminate={multiselectFeat.toggleAllFeat.isSomeSelected()}
                                        onChange={multiselectFeat.toggleAllFeat.toggle}
                                    />
                                </div>
                            }
                        </th>
                    }
                    {
                        expandFeat !== null &&
                        <th colSpan={1}></th>
                    }
                    {
                        columns.map((column) => (
                            <th key={column.attr}
                                hidden={column?.hide === true}
                                colSpan={column.span}
                                className={'font-light ' + (column.attr === firstColumn ? style.firstColStyle : '')}
                                onClick={() => setSortBy && setSortBy(column)}>
                                <div className='flex items-center'>
                                    <div>
                                        {column.name}
                                    </div>
                                    {
                                        sortBy &&
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    }
                                </div>
                            </th>
                        ))
                    }
                    {
                        actionColFeat !== null &&
                        <th colSpan={actionColFeat?.colSpan}>
                            {actionColFeat?.actionColumn}
                        </th>
                    }
                </tr>
                </thead>
                <tbody>
                {
                    !loadingFeat?.loading && rowsData !== undefined && rowsData.map((row) =>
                        <RowItem<T> key={get(row, keyColumn)}
                                    style={style}
                                    rowHighlightFeat={rowHighlightFeat}
                                    firstColumn={firstColumn}
                                    cellDisplayFunc={cellDisplayFunc}
                                    expandFeat={expandFeat}
                                    item={row}
                                    setSelectedFunc={setSelectedFunc}
                                    multiSelectFeat={multiselectFeat}
                                    actionFeat={actionColFeat}
                                    columns={columns}
                                    selected={get(selected, keyColumn) === get(row, keyColumn)}
                        />
                    )
                }
                </tbody>
            </table>
            {
                loadingFeat && loadingFeat.loading && loadingFeat.loadingDisplayElement
            }
            {
                rowsData !== undefined && rowsData.length === 0 && !loadingFeat?.loading && emptyMessage
            }
        </div>

    );
}

type RowItemProps<T> = {
    item: T;
    cellDisplayFunc: (item: T, column: Column<T>, selected: boolean) => string | React.ReactElement;

    expandFeat: EnrichTableExpandFeat<T> | null;
    rowHighlightFeat: RowHighlightFeat<T> | null;

    setSelectedFunc?: null | ((selected: T) => void);
    selected: boolean;

    multiSelectFeat?: EnrichTableMultiSelectFeat<T> | null;
    actionFeat?: EnrichTableActionColFeat | null;

    firstColumn?: DeepKeyof<T> | null;
    columns: Readonly<Column<T>[]>;
    style: EnrichedTableStyle;
}

const RowItem = <T, >({
                          item,
                          selected,
                          columns,
                          style,
                          setSelectedFunc,
                          cellDisplayFunc,
                          rowHighlightFeat,
                          expandFeat,
                          multiSelectFeat = null,
                          actionFeat = null,

                          firstColumn = null
                      }: RowItemProps<T>) => {
    let showExpand = false;
    const shouldHighlight = rowHighlightFeat !== null && rowHighlightFeat.shouldHighlight(item);
    let expandColSpan = columns.map((item) => item.span).reduce((a, b) => a + b) + 1;
    if (multiSelectFeat !== null) {
        expandColSpan++;
    }
    if (expandFeat !== null) {
        showExpand = expandFeat.shouldShowExpanded!(item);
    }

    let rowStyle = selected ? style.trRowSelected : style.trRow
    if (shouldHighlight) {
        rowStyle = `${rowStyle} ${rowHighlightFeat.highlightCss.row}`
        if (rowHighlightFeat?.highlightColorByItem) {
            rowStyle = `${rowStyle} ${rowHighlightFeat.highlightColorByItem(item).row}`
        }
    }
    let columnStyle = (column: Column<T>) => {
        if (shouldHighlight) {
            if (rowHighlightFeat?.highlightColorByItem) {
                return `${rowHighlightFeat?.highlightColorByItem(item).column} py-5 ${(column.attr === firstColumn ? style.firstColStyle : '')}`;
            }
            return `${rowHighlightFeat?.highlightCss.column} py-5 ${(column.attr === firstColumn ? style.firstColStyle : '')}`;
        }
        return `py-5 ${(column.attr === firstColumn ? ' pl-4' : '')}`;
    }

    return (
        <>
            <tr className={rowStyle}>
                {
                    multiSelectFeat !== null &&
                    <td colSpan={1}>
                        <div className="flex items-center justify-center">
                            <Checkbox
                                checked={multiSelectFeat.isSelected(item) && !multiSelectFeat.shouldDisableSelected(item)}
                                disabled={multiSelectFeat.shouldDisableSelected(item)}
                                onChange={() => multiSelectFeat.toggleSelect(item)}/>
                        </div>
                    </td>
                }
                {
                    expandFeat !== null &&
                    <td colSpan={1}>
                        {
                            !showExpand && expandFeat.itemHaveExpand(item) &&
                            <div className="flex items-center justify-center">
                                <IconButton icon={ChevronDownIcon}
                                            appearance={"minimal"}
                                            onClick={() => expandFeat.setExpandedFunc!(item)}/>
                            </div>
                        }
                        {
                            showExpand && expandFeat.itemHaveExpand(item) &&
                            <div className="flex items-center justify-center">
                                <IconButton icon={ChevronUpIcon}
                                            appearance={"minimal"}
                                            onClick={() => expandFeat.setExpandedFunc!(item)}/>
                            </div>
                        }
                    </td>
                }
                {
                    columns.map((column) => (
                        <td
                            colSpan={column.span}
                            key={column.name}
                            hidden={column?.hide === true}
                            className={columnStyle(column)}
                            onClick={() => setSelectedFunc ? setSelectedFunc(item) : () => {
                            }}>
                            {
                                cellDisplayFunc(item, column, selected)
                            }
                        </td>
                    ))
                }
                {
                    actionFeat !== null &&
                    <td colSpan={actionFeat.colSpan}/>
                }
            </tr>
            {expandFeat && showExpand &&
                <tr>
                    <td colSpan={expandColSpan}
                        className="transition-all duration-500">
                        {
                            expandFeat.expandDisplayFunc(item)
                        }
                    </td>
                </tr>
            }
        </>
    )
}
